import React, { useState } from 'react';

interface StyleOption {
  key: string;
  name: string;
  description: string;
}

interface HarshnessOption {
  level: number;
  name: string;
  description: string;
}

interface LengthOption {
  key: string;
  name: string;
  description: string;
}

// Hardcoded client-side (previously fetched from GET /api/prompt-components).
// Keys must match the switches in roastService.ts.
const STYLES: StyleOption[] = [
  { key: 'meme', name: 'Meme Lord', description: 'Meme-filled roasting with creative analogies' },
  { key: 'professional', name: 'Professional', description: 'Professional academic critique' },
  { key: 'reviewer_2', name: 'Reviewer #2', description: 'Reviewer #2 at a top conference (brutal but academic)' },
];

const HARSHNESS_LEVELS: HarshnessOption[] = [
  { level: 1, name: 'Nice & Supportive', description: 'Very nice, kind, supportive' },
  { level: 2, name: 'Compliment Sandwich', description: 'Balanced critique with positives' },
  { level: 3, name: 'Critical & Direct', description: 'Direct, serious concerns' },
  { level: 4, name: 'Nuclear Option', description: 'Assume bad faith, maximum brutality' },
];

const LENGTHS: LengthOption[] = [
  { key: 'tweet', name: 'Tweet', description: 'Twitter-length roast (280 chars)' },
  { key: 'short', name: 'Short', description: 'Quick summary (1-2 paragraphs)' },
  { key: 'standard', name: 'Standard', description: 'Standard review (default)' },
  { key: 'deep_dive', name: 'Deep Dive', description: 'Comprehensive deep-dive analysis' },
];

interface PromptSelectorProps {
  components: {
    style: string;
    harshness: number;
    length: string;
    customUserPrompt: string | undefined;
  };
  onChange: (components: {
    style: string;
    harshness: number;
    length: string;
    customUserPrompt: string | undefined;
  }) => void;
  isComponentMode: boolean;
}

const PromptSelector: React.FC<PromptSelectorProps> = ({ components, onChange, isComponentMode }) => {
  const [showCustomPrompt, setShowCustomPrompt] = useState(false);

  if (!isComponentMode) {
    return null;
  }

  return (
    <div className="space-y-6 mb-6 p-4 bg-neutral-gray rounded-lg border border-neutral-light">
      {/* Style Selection */}
      <div>
        <h2 className="text-white font-semibold mb-3">Review Style</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {STYLES.map((style) => (
            <button
              key={style.key}
              type="button"
              onClick={() => onChange({ ...components, style: style.key })}
              className={`p-3 rounded transition-all text-left ${
                components.style === style.key
                  ? 'bg-critique-primary text-white'
                  : 'bg-neutral-darker text-white border border-neutral-light hover:border-critique-primary'
              }`}
            >
              <div className="font-semibold">{style.name}</div>
              <div className="text-sm opacity-80 mt-1">{style.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Harshness Level */}
      <div>
        <h2 className="text-white font-semibold mb-3">Harshness Level</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {HARSHNESS_LEVELS.map((level) => (
            <button
              key={level.level}
              type="button"
              onClick={() => onChange({ ...components, harshness: level.level })}
              className={`p-3 rounded transition-all text-left ${
                components.harshness === level.level
                  ? 'bg-critique-primary text-white'
                  : 'bg-neutral-darker text-white border border-neutral-light hover:border-critique-primary'
              }`}
            >
              <div className="font-semibold">{level.name}</div>
              <div className="text-sm opacity-80 mt-1">{level.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Review Length */}
      <div>
        <h2 className="text-white font-semibold mb-3">Review Length</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {LENGTHS.map((length) => (
            <button
              key={length.key}
              type="button"
              onClick={() => onChange({ ...components, length: length.key })}
              className={`p-3 rounded transition-all text-left ${
                components.length === length.key
                  ? 'bg-critique-primary text-white'
                  : 'bg-neutral-darker text-white border border-neutral-light hover:border-critique-primary'
              }`}
            >
              <div className="font-semibold">{length.name}</div>
              <div className="text-sm opacity-80 mt-1">{length.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Custom User Prompt */}
      <div>
        <button
          type="button"
          onClick={() => setShowCustomPrompt(!showCustomPrompt)}
          className="text-critique-primary underline text-sm hover:text-critique-accent transition-colors"
        >
          {showCustomPrompt ? 'Hide' : 'Add'} custom instructions
        </button>

        {showCustomPrompt && (
          <div className="mt-3">
            <textarea
              className="w-full px-4 py-3 bg-neutral-darker text-white rounded border border-neutral-light focus:outline-none focus:border-critique-primary transition-all placeholder:text-neutral-lighter"
              placeholder="Add any specific instructions or areas of focus..."
              value={components.customUserPrompt || ''}
              onChange={(e) => onChange({ ...components, customUserPrompt: e.target.value })}
              rows={3}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptSelector;
